import { c as _c } from "react/compiler-runtime";
import { DistributionsSharedCfg, DistributionChart, DistributionLabel, binForecast, ForecastBinBar, binForecastScale, BinForecast, Dist, DistItem, BinWrap, isForecastBinsEmpty, BinForecasts, BinWrapProps } from '../../chart';
import { QuestionCfg, ResolutionValue } from '../../forecast';
import { compactByProps } from '../../collection';
import { concat, isNullOrEmpty, notNullOrEmpty } from '../../collection';
import { doFn, doSafe, doTo } from '../../promise';
import { Fmt } from '../../forecast/Fmt';
import { fmtNum } from '../../format';
import { max } from '../../agg';
import { Rnge } from '../../range';
import { Bounds } from '../../forecast/bounds';
import { LabelEmptyList } from '../../ui';
import { Forecast as F, ForecastQuestionCfgAmount, Logistic, ResolutionForecast } from '../forecast';
import { ScaleLinear, scaleLinear } from 'd3-scale';
import { useSafeMantineTheme } from '@mantine/core';
import { ForecastChartProps, resultColorFn, resolutionColor, ForecastChartItem, QRNoForecastsYet } from './QuestionResults';
import { isNonNullish, pick, pipe } from 'remeda';
import { useMemo } from 'react';
import { useElementSize, useEmSize, useMemoValue } from '../../ui';
import { TbArrowBarLeft, TbArrowBarRight, TbArrowBarToLeft, TbArrowBarToRight } from 'react-icons/tb';
import { QRComparisons } from './QRComparisons';
import { match, P } from 'ts-pattern';
const areaHeight = 80;
const marginDefault = {
  top: 2,
  right: 16,
  left: 16
};
const useDistribution = ({
  question,
  forecasts,
  chart: chartRaw,
  labels,
  resolutionTitle,
  baselineDist: baselineDistProps,
  relative: relativeRaw,
  tipTitle,
  debugName
}: {
  question: Pick<QuestionCfg, 'defaultBounds' | 'hardBounds' | 'unitLabel' | 'numberFormat' | 'title' | 'chart' | 'amountType' | 'aggVersion'>;
  forecasts?: ResolutionForecast[];
  chart: Partial<DistributionsSharedCfg>;
  labels?: DistributionLabel[];
  resolutionTitle?: string;
  relative: boolean;
  baselineDist?: DistProps;
  tipTitle?: string;
  debugName?: string;
}): DistProps => {
  const {
    defaultBounds: bounds,
    unitLabel,
    numberFormat,
    title,
    amountType
  } = question;
  const relativeToX = relativeRaw ? baselineDistProps?.dist?.percentile50 : undefined;
  const relative = relativeRaw && relativeToX != null;
  const format = useMemoValue(Fmt.getOption({
    forecastType: 'amount',
    numberFormat,
    unitLabel,
    mode: relative ? 'relative' : 'absolute',
    amountType
  }));
  const distsRaw = useMemo(() => forecasts ? compactByProps(forecasts.map(f => ({
    forecast: f,
    dist: F.amountDist({
      f: f.cfg,
      q: question
    })
  })), ['dist']) : undefined, [forecasts, question]);
  const chart = useMemoValue({
    bounds: Bounds.toTuple(bounds),
    unitLabel,
    axisLabel: unitLabel,
    formatFirst: format.default.fmtFull,
    format: format.default.fmtFull,
    formatShort: format.short.fmtFull,
    ...chartRaw
  } as DistributionsSharedCfg);
  const dist = useMemo(() => {
    const res = distsRaw ? pipe(Dist.agg(distsRaw.map(d => d.dist), {
      displayBounds: Bounds.toTuple(bounds),
      hardBounds: Bounds.toTuple(question.hardBounds),
      aggVersion: question.aggVersion,
      debugName,
      title
    }), d_0 => ({
      ...d_0,
      labels: concat(labels, Logistic.label50th(d_0, {
        format: format.default,
        color: chart?.primaryColor,
        subTitle: resolutionTitle,
        relativeToX
      }), Logistic.percentileLabels(d_0)),
      tipTitle
    })) : undefined;
    return res;
  }, [distsRaw, bounds, format, chart, resolutionTitle, relativeToX, question.aggVersion]);
  return {
    chart,
    dist
  };
};
type DistProps = {
  chart: DistributionsSharedCfg;
  dist: DistItem | undefined;
};
export const binDists = (dists: {
  dist: Pick<Dist.DiscreteDistribution, 'percentile50'>;
  notes?: string;
  id: string;
}[], scale: ScaleLinear<number, number>) => binForecast({
  forecasts: dists.map(f => ({
    value: f.dist.percentile50!,
    // get this users baseline 50th percentile from the baseline distributions and return the relative value.. actually do this before binning
    notes: f.notes,
    id: f.id
  })),
  domain: scale
});
export type BinDists = ReturnType<typeof binDists>;
type ForecastBinDisplay = BinForecast & {
  id: string;
};
export const QRAmount = ({
  cfg,
  forecasts,
  resolution,
  questionAgg,
  noteCustom,
  showHelp,
  hideBaselineLabel,
  hideAxisLabels,
  baselineForecasts,
  shouldVizRelative,
  code,
  tag,
  width: widthRaw,
  resolutionValue,
  comparisons,
  chartProps,
  cumulative,
  debugName
}: ForecastChartProps<BinForecast, ForecastQuestionCfgAmount> & {
  baselineForecasts?: Pick<ForecastChartItem, 'cfg' | 'forecasts' | 'shouldVizRelative' | 'group' | 'resolution'>;
  resolutionValue?: ResolutionValue;
  chartProps?: Partial<DistributionsSharedCfg>;
  cumulative?: boolean;
  debugName?: string;
}) => {
  const {
    colors
  } = useSafeMantineTheme();
  const {
    width: containerWidth,
    ref: containerRef
  } = useElementSize();
  const width = widthRaw ?? containerWidth;
  const getResColor = resultColorFn({
    colors,
    cfg: cfg.colors
  });
  const primaryColor = resolutionColor({
    cfg,
    resolution,
    code
  }, colors);
  const question = useMemoValue(pick(cfg, ['defaultBounds', 'hardBounds', 'unitLabel', 'numberFormat', 'title', 'chart', 'amountType', 'aggVersion']));
  const baselineDistRaw = useDistribution({
    question,
    forecasts: baselineForecasts?.forecasts,
    chart: {
      primaryColor: baselineForecasts?.resolution.color
    },
    relative: false,
    tipTitle: 'baseline',
    debugName
  });
  const baselineDist = useMemo(() => baselineDistRaw.dist ? {
    ...baselineDistRaw,
    dist: {
      ...baselineDistRaw.dist,
      labels: [],
      color: baselineForecasts?.resolution.color,
      lineProps: {
        strokeDasharray: '8',
        strokeOpacity: 0.5
      }
    } as DistItem
  } : undefined, [baselineDistRaw.dist]);
  const {
    chart,
    dist
  } = useDistribution({
    question,
    forecasts,
    chart: {
      primaryColor
    },
    labels: [...(doSafe(questionAgg?.group, x => [Logistic.labelComparison({
      x,
      title: hideBaselineLabel ? '' : 'baseline',
      color: getResColor('question')
    })]) ?? []), resolutionValue?.status == 'value' && resolutionValue.num != null ? {
      type: 'xBottom',
      x: resolutionValue.num,
      title: '',
      color: 'var(--mantine-color-red-6)',
      lineProps: {
        strokeWidth: 3,
        opacity: 1,
        strokeDasharray: undefined
      }
    } as DistributionLabel : undefined].filter(isNonNullish),
    resolutionTitle: resolution.label,
    baselineDist,
    relative: shouldVizRelative
  });
  const pMax = useMemo(() => max(concat(dist!.pts, baselineDist?.dist?.pts).map(p => p.p)), [dist, baselineDist?.dist]);
  const {
    defaultBounds: boundsRaw,
    hardBounds
  } = question;
  const bounds = Bounds.withDefaults(boundsRaw, Bounds.defaultForType(question.amountType)).bounds;
  const emSize = useEmSize();
  const sizeProps = doFn(() => {
    const tailSpace = emSize.getTextWidth('0.0%', {
      font: tailFont
    }) + 12;
    const margin = {
      ...marginDefault,
      left: dist?.left == null ? marginDefault.left : tailSpace,
      right: dist?.right == null ? marginDefault.right : tailSpace
    };
    const yRange = [areaHeight + margin.top, margin.top];
    const xScale = scaleLinear(Bounds.toTuple(bounds), [margin.left, width - margin.right]);
    const yScale = scaleLinear([0, pMax], yRange);
    return {
      xScale,
      yScale,
      margin
    };
  });
  const bins = useMemo(() => binForecast({
    domain: sizeProps.xScale,
    forecasts: forecasts.map(({
      cfg: cfg_0,
      ...rest
    }) => ({
      ...rest,
      notes: cfg_0.notes
    })),
    amountType: cfg.amountType
  }), [forecasts]);
  const commonDistProps = {
    chart,
    ...sizeProps
  };
  const {
    unitLabel,
    numberFormat,
    amountType
  } = cfg;
  const format = useMemoValue(Fmt.getOptions({
    forecastType: 'amount',
    numberFormat,
    unitLabel,
    amountType
  }));
  if (bounds == null) console.debug('qr', {
    cfg,
    title: question.title,
    id: forecasts[0]?.question_id,
    sqid: forecasts[0]?.subquestion_id
  });
  return <div ref={containerRef} id='qramount' className='select-none relative w-full grow'>
      {match({
      width,
      dist
    }).with({
      width: P.number.gt(0),
      dist: P.not(P.nullish)
    }, ({
      dist: dist_0
    }) => {
      const tailUIProps = (side: 'left' | 'right') => {
        const {
          left,
          right
        } = dist_0;
        const pctWhole = doSafe(side == 'left' ? left?.pct : right?.pct, p_0 => p_0 * 100);
        if (!question.chart.showTails || pctWhole == null || pctWhole < 1) return undefined;
        const hardBound = hardBounds?.[side == 'left' ? 'min' : 'max'];
        const mode = hardBound && Rnge.between(hardBound, Bounds.toTuple(bounds)) ? 'bound' : 'tail';
        const x_0 = mode == 'tail' ? bounds[side == 'left' ? 'min' : 'max'] : hardBound!;
        return {
          format,
          binTails: bins.tails,
          color: primaryColor,
          noteCustom,
          side,
          mode,
          pctWhole,
          xScale: sizeProps.xScale,
          x: x_0
        } satisfies TailProps;
      };
      return <>
              {tag && <div className='absolute right-0 top-0'>{tag}</div>}
              {forecasts.length > 0 ? <>
                  <div className='relative '>
                    <DistributionChart {...commonDistProps} dist={dist_0} dists={baselineDist ? [baselineDist.dist] : undefined} chart={{
              ...chart,
              formats: format.absolute,
              ...chartProps
            }} hideLabels={hideAxisLabels} yAccessor={cumulative ? d => d.pAcc : undefined} debugName={debugName} tipOffsetY={!isForecastBinsEmpty(bins) ? '1rem' : undefined} />
                    <>
                      {doSafe(tailUIProps('left'), p_1 => <Tail {...p_1} />)}
                      {doSafe(tailUIProps('right'), p_2 => <Tail {...p_2} />)}
                    </>
                  </div>

                  {!isForecastBinsEmpty(bins) && <>
                      <ForecastBinBar<ForecastBinDisplay> {...{
              bins: bins.bins,
              scale: sizeProps.xScale,
              format
            }} scaleBinColor={binForecastScale({
              bins: bins.bins,
              color: primaryColor,
              colors
            })} showValue={false} noteCustom={noteCustom} showHelp={showHelp} />
                    </>}

                  {comparisons && <QRComparisons comparisons={comparisons} scale={sizeProps.xScale} fmt={format.absolute.default} />}
                </> : <></>}
            </>;
    }).otherwise(() => <></>)}
    </div>;
};
const tailFont = {
  fontSize: '0.9em',
  fontFamily: 'var(--font-family-numbers)',
  fontWeight: 'bold'
};
type TailProps = {
  pctWhole?: number;
  mode: 'bound' | 'tail';
  side: 'left' | 'right';
  binTails: BinForecasts['tails'];
  color: string;
  xScale: (x: number) => number;
  x: number;
  className?: string;
} & Pick<BinWrapProps, 'noteCustom' | 'format'>;
const Tail = t0 => {
  const $ = _c(21);
  const {
    pctWhole,
    className,
    side,
    mode,
    binTails: tails,
    color,
    noteCustom,
    format,
    xScale,
    x
  } = t0;
  let t1;
  if ($[0] !== side || $[1] !== tails) {
    t1 = tails?.[side] ?? {};
    $[0] = side;
    $[1] = tails;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    forecasts
  } = t1;
  let t2;
  if ($[3] !== side || $[4] !== x || $[5] !== xScale) {
    t2 = x != null ? {
      left: xScale(x)
    } : side == "left" ? {
      left: 0
    } : {
      right: 0
    };
    $[3] = side;
    $[4] = x;
    $[5] = xScale;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const locationStyle = t2;
  let t3;
  if ($[7] !== format?.absolute) {
    t3 = x_0 => Fmt.fmt(x_0, format?.absolute.default);
    $[7] = format?.absolute;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const fmtX = t3;
  let t4;
  if ($[9] !== className || $[10] !== color || $[11] !== fmtX || $[12] !== forecasts || $[13] !== format || $[14] !== locationStyle || $[15] !== mode || $[16] !== noteCustom || $[17] !== pctWhole || $[18] !== side || $[19] !== x) {
    t4 = isNullOrEmpty(forecasts) && pctWhole == null ? undefined : <BinWrap {...{
      noteCustom,
      format
    }} forecasts={forecasts ?? []} hideValueLabels={true} tipTitle={pctWhole == null ? null : <div><b>{fmtNum(pctWhole)}</b> percentage of the probability is{" "}{match(mode).with("tail", () => `${side == "left" ? "below" : "above"} ${x != null ? fmtX(x) : ""}`).with("bound", () => `at ${x != null ? fmtX(x) : "bound"}`).exhaustive()}</div>}><div id="tail" className={className} style={{
        color: "var(--mantine-color-gray-4)",
        position: "absolute",
        top: "50%",
        transform: `translate(${side == "left" ? "calc(-100% - 4px)" : "4px"}, -70%)`,
        ...locationStyle
      }}><div className="flex flex-col" style={{
          alignItems: side == "left" ? "end" : "start"
        }}><TailIcon {...{
            side,
            mode
          }} />{pctWhole != null && <div style={{
            ...tailFont,
            color: notNullOrEmpty(forecasts) ? color : undefined
          }}>{fmtNum(pctWhole)}%</div>}</div></div></BinWrap>;
    $[9] = className;
    $[10] = color;
    $[11] = fmtX;
    $[12] = forecasts;
    $[13] = format;
    $[14] = locationStyle;
    $[15] = mode;
    $[16] = noteCustom;
    $[17] = pctWhole;
    $[18] = side;
    $[19] = x;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
};
const iconProps = {
  style: {},
  size: 18
};
export const TailIcon = ({
  side,
  mode
}: {
  mode: 'bound' | 'tail';
  side: 'left' | 'right';
}) => match({
  side,
  type: mode
}).with({
  side: 'left',
  type: 'tail'
}, () => <TbArrowBarLeft {...iconProps} />).with({
  side: 'right',
  type: 'tail'
}, () => <TbArrowBarRight {...iconProps} />).with({
  side: 'left',
  type: 'bound'
}, () => <TbArrowBarToRight {...iconProps} />).with({
  side: 'right',
  type: 'bound'
}, () => <TbArrowBarToLeft {...iconProps} />).exhaustive();